import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";

type Variant = "global" | "group";

export const UserBulkActions = ({
  selectedItems,
  deleteUser,
  variant = "group",
}: {
  selectedItems: Map<string, UserDto>;
  deleteUser: (userUuid: string) => Promise<unknown>;
  variant?: Variant;
}) => {
  const [dialogs, setDialogs] = useState<Record<string, boolean>>({
    delete: false,
  });
  const users = Array.from(selectedItems.values());
  const hasSelectedUsers = users.length > 0;

  const handleDelete = async () => {
    if (!hasSelectedUsers) return;
    for (const user of users) {
      await deleteUser(user.uuid);
    }
  };

  const userPlural = new Intl.PluralRules().select(users.length);

  return (
    <>
      <Tooltip open={hasSelectedUsers ? undefined : false}>
        <TooltipContent>
          Delete {users.length} {userPlural === "one" ? "user" : "users"}
        </TooltipContent>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={!hasSelectedUsers}
            onClick={() => setDialogs((prev) => ({ ...prev, delete: true }))}
            aria-label="Delete"
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </TooltipTrigger>
      </Tooltip>
      <AlertDialog
        open={dialogs.delete}
        onOpenChange={() =>
          setDialogs((prev) => ({ ...prev, delete: !prev.delete }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {users.length} selected{" "}
              {userPlural === "one" ? "user" : "users"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove{" "}
              {users.length} selected {userPlural === "one" ? "user" : "users"}
              {variant === "group" ? " from the group" : ""}. You'll be
              removing:
              <ul className="mt-2 list-disc pl-5">
                {users.map((user) => (
                  <li key={user.uuid}>{user.name || user.email}</li>
                ))}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              asChild
            >
              <Button size="sm">Remove</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
